// @ts-nocheck
import filter from "@insite/mobius/DynamicDropdown/filter";
/* Starter Custom Code
import FormField, {
    FormFieldComponentProps,
    FormFieldIcon,
    FormFieldPresentationProps,
    FormFieldSizeVariant,
} from "@insite/mobius/FormField";
*/
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
// import { BaseTheme } from "@insite/mobius/globals/baseTheme"; // Store Custom Code
import { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
// import Popover, { ContentBodyProps, OverflowWrapperProps } from "@insite/mobius/Popover"; // Store Custom Code
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { MobiusStyledComponentPropsWithRef } from "@insite/mobius/utilities/MobiusStyledComponentProps";
import safeColor from "@insite/mobius/utilities/safeColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

/* Starter Custom Code - Start */
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import FormField, {
    FormFieldClickable,
    FormFieldComponentProps,
    FormFieldIcon,
    FormFieldPresentationProps,
    FormFieldSizeVariant,
} from "@store/MobiusOverrides/FormField/FormField";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import Popover, { ContentBodyProps, OverflowWrapperProps, PopoverProps } from "@insite/mobius/Popover";
import { BaseTheme, ThemeTransitionDuration } from "@insite/mobius/globals/baseTheme";
import { dynamicDropdownGap } from "@store/Themes/common/styleguide/components/forms/dynamicDropdown";

const defaultTransitionDuration: keyof ThemeTransitionDuration = "none";
/* Store Custom Code - End */

export interface DynamicDropdownPresentationProps
    extends Omit<FormFieldPresentationProps<DynamicDropdownComponentProps>, "cssOverrides"> {
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: FormFieldPresentationProps<DynamicDropdownComponentProps>["cssOverrides"] & {
        dropdownWrapper?: StyledProp<DynamicDropdownComponentProps>;
        list?: StyledProp<ContentBodyProps>;
        /** Specific style options for loading row, if not provided, falls back on 'option'. */
        loading?: StyledProp<DynamicDropdownComponentProps>;
        /** Specific style options for moreOption row, if not provided, falls back on 'option'. */
        moreOption?: StyledProp<DynamicDropdownComponentProps>;
        /** Specific style options for noOptions row, if not provided, falls back on 'option'. */
        noOptions?: StyledProp<DynamicDropdownComponentProps>;
        option?: StyledProp<DynamicDropdownComponentProps>;
        selectedText?: StyledProp<DynamicDropdownComponentProps>;
    };
    /** Props to be passed into the inner Icon component.
     * @themable */
    iconProps?: IconPresentationProps;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** Text for empty input field.
     * @themable */
    placeholder?: string;
    /** Bool to be used to disable typing in dropdown field */
    selectOnly?: boolean;
    /** Props to be passed into the inner LoadingSpinner component when loading.
     * @themable */
    spinnerProps?: LoadingSpinnerProps;
    /** Background color of the form element */
    backgroundColor?: string;
    // Starter Custom Code
    /** Props to be passed into the clear clickable component.
     * @themable */
    clearClickable?: ClickablePresentationProps;
    /** Props to be passed into the clear Icon component.
     * @themable */
    clearIcon?: IconPresentationProps;
    // Store Custom Code
    useOverlay?: boolean;
}

export interface OptionObject {
    optionValue?: string;
    optionText: string;
    searchString?: string;
    rowChildren?: React.ReactNode;
    disabled?: boolean;
}

interface DynamicDropdownComponentProps extends Partial<FormFieldComponentProps> {
    /** Flag to indicate whether data is loading on the component. */
    isLoading?: boolean;
    /** Error message to be displayed below the dropdown box. */
    error?: React.ReactNode;
    /** Hint text to be displayed below the dropdown box. */
    hint?: React.ReactNode;
    /** Function in the following format to be used to filter the options in the dropdown.
     * @param {object} option An object describing the option.
     * @param {string} option.value The value of the menu item.
     * @param {string} [option.data] Any additional string data to be searched by the dynamic dropdown.
     * @param {string} rawInput The string being searched for within the option.
     * @return {boolean} Whether the option includes the rawInput.
     */
    filterOption?: typeof filter;
    /**
     * Unique id to be passed into the `<input>` element.
     * If not provided, a random id is assigned (an id is required for accessibility purposes).
     */
    uid?: string;
    /** Label to be displayed above the input. */
    label?: React.ReactNode;
    /** Adds an asterisk to the input's label (if provided). Additionally, governs whether an error if a disabled option is selected */
    required?: boolean;
    /** An optional final list item for the dynamic dropdown. If only a subset of options is displayed on load, this component
     * can be used to indicate 'search for more options' or can contain a link, etcetera, to provide further functionality. */
    moreOption?: React.ReactNode;
    /** Function that will be called after the dropdown is closed. */
    onClose?: (event?: React.MouseEvent | React.KeyboardEvent | Event) => void;
    /** A callback function that will be called when the typed input is changed */
    onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Function that will be called after the dropdown is opened. */
    onOpen?: (event?: React.MouseEvent | React.KeyboardEvent | Event) => void;
    /** Function that will be called when the selected item is changed. */
    onSelectionChange?: (value?: string) => void;
    /** An array of objects describing the options available in the dropdown. */
    options: OptionObject[];
    /** Value of the selected option */
    selected?: string;
    /** A callback function that will be called when any key is pressed */
    onKeyPress?: (event: React.KeyboardEvent) => void;
    /** If the user has not typed anything into the field to search, and there are no options, this lets you hide the "no options" item */
    hideNoOptionsIfEmptySearch?: boolean;
    /** Value of the search text */
    search?: string;
    /**
     * @deprecated Not needed anymore
     */
    skipPreventDefault?: boolean;
    // Starter Custom Code
    /** Show a clear button, the clearOnClick property must also be provided */
    showClear?: boolean;
    /** The onClick event for the clear button */
    clearOnClick?: React.EventHandler<React.MouseEvent>;
    // Store Custom Code
    scrollElement?: HTMLElement | null;
}

interface DynamicDropdownState {
    isOpen: boolean;
    focusable: OptionObject[];
    renderOptions?: JSX.Element[];
    focusedOption?: OptionObject;
    uid: string;
    selected?: string;
    selectedText?: string;
    typedInput: string;
}

/* Starter Custom Code - next line - export */
export type OptionProps = MobiusStyledComponentPropsWithRef<
    "div",
    { ref?: React.RefObject<HTMLDivElement | null> },
    {
        _sizeVariant: FormFieldSizeVariant;
        focused?: boolean;
        disabled?: boolean;
        selected?: boolean;
        noHover?: boolean;
        css?: StyledProp<any>;
        key: string;
        value: string;
        uid?: string;
        onClick?: (value: string) => void;
        "aria-selected"?: boolean;
    }
>;

export type DynamicDropdownProps = DynamicDropdownPresentationProps &
    DynamicDropdownComponentProps &
    ThemeProps<BaseTheme>;

const ESC_KEY = 27;
const DOWN_KEY = 40;
const UP_KEY = 38;
const TAB_KEY = 9;
const ENTER_KEY = 13;

const scrollTo = (el: HTMLElement, top: number) => {
    el.scrollTop = top;
};

/* Starter Custom Code - Next Line - styled.li */
const Option = styled.li<OptionProps>`
    padding: ${({ _sizeVariant }) => (_sizeVariant === "small" ? "5px 11px" : "9px 11px") /* Starter Custom Code */};
    font-size: ${({ _sizeVariant }) => sizeVariantValues[_sizeVariant].fontSize}px;
    ${({ focused, disabled, selected, theme, noHover }) => {
        let background = get(theme, "colors.common.background");
        let text = get(theme, "colors.common.backgroundContrast");
        const primary = get(theme, "colors.primary.main");
        const hover = safeColor(primary).rgb().fade(0.8).string();
        if (disabled) {
            background = get(theme, "colors.common.accent");
            text = get(theme, "colors.text.disabled");
        } else if (selected) {
            background = primary;
            text = get(theme, "colors.primary.contrast");
        } else if (focused) {
            background = hover;
        }
        return css`
            background: ${background};
            color: ${text};
            ${!disabled &&
            !noHover &&
            css`
                &:hover {
                    background: ${hover};
                    color: ${get(theme, "colors.common.backgroundContrast")};
                }
            `}
            ${disabled ? "cursor: not-allowed;" : "cursor: pointer;"}
        `;
    }}
    transition: all 0.3s ease-in-out;
    ${injectCss}
`;

type SizeVariant = { _sizeVariant: "small" | "default" };

const SelectedText = styled(Typography as any)<SizeVariant>`
    pointer-events: none;
    position: absolute;
    padding: ${({ _sizeVariant }) => (_sizeVariant === "small" ? "5px 24px 5px 11px" : "9px 24px 9px 11px")};
    top: 0;
    left: 0;
    white-space: nowrap;
    overflow: hidden;
    width: 100%; /* Starter Custom Code */
    text-overflow: ellipsis;
    /* Starter Custom Code - Start */
    height: 100%;
    &:not(#important) {
        background-color: transparent;
        border-color: transparent;
    }
    input:disabled + && {
        color: ${getColor("text.disabled")};
    }
    /* Starter Custom Code - End */
    ${injectCss}
`;

const PopoverTriggerInput = styled.input`
    /* Starter Removed - padding-right */
    /* Starter Custom Code - Start */
    &[readonly] {
        cursor: default;
    }
    /* Starter Custom Code - End */
    ${injectCss}
`;

const listCssBuilder = (
    additionalCss: StyledProp<ContentBodyProps>,
    sizeVariant: FormFieldSizeVariant,
) => css<ContentBodyProps>`
    background: ${getColor("common.background")};
    border: 1px solid ${getColor("common.border")};
    padding-left: 0;
    top: ${sizeVariantValues[sizeVariant].height - 1}px;
    width: calc(100% - 2px);
    margin-bottom: 15px;
    overflow-y: auto;
    /* Starter Custom Code - Start */
    border-bottom-right-radius: ${sizeVariantValues[sizeVariant].borderRadius}px;
    border-bottom-left-radius: ${sizeVariantValues[sizeVariant].borderRadius}px;
    /* Starter Custom Code - End */
    ${additionalCss as any}
`;

/**
 * A searchable dropdown.
 */
class DynamicDropdown extends React.Component<DynamicDropdownProps & HasDisablerContext, DynamicDropdownState> {
    static displayName = "DynamicDropdown";
    private spreadProps: Function;
    private applyStyledProp: Function;
    private sizeVariant: FormFieldSizeVariant;

    static defaultPlaceholder = "Type to search";
    static defaultProps: Partial<DynamicDropdownProps> = {
        isLoading: false,
        filterOption: filter,
        placeholder: DynamicDropdown.defaultPlaceholder,
    };

    constructor(props: DynamicDropdownProps & HasDisablerContext) {
        super(props);
        const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(props, {
            component: "dynamicDropdown",
            category: "formField",
        });
        this.applyStyledProp = applyStyledProp;
        this.spreadProps = spreadProps;
        this.sizeVariant = applyProp("sizeVariant", "default");
        this.state = {
            isOpen: false,
            focusable: props.isLoading ? [] : props.options,
            focusedOption: undefined,
            uid: props.uid || uniqueId(),
            selected: props.selected || undefined,
            selectedText: "",
            typedInput: props.search || "",
        };
    }

    input = React.createRef<HTMLElement>();

    list = React.createRef<HTMLUListElement>();

    focused = React.createRef<HTMLDivElement>();

    popover = React.createRef<typeof Popover>();

    UNSAFE_componentWillReceiveProps(nextProps: DynamicDropdownProps) {
        if (nextProps.selected !== this.props.selected) {
            const newSelectedText =
                nextProps.selected && nextProps.options
                    ? nextProps.options.find(o => o.optionValue === nextProps.selected)?.optionText
                    : "";
            this.setState({ selected: nextProps.selected, selectedText: newSelectedText });
        }
        if (nextProps.options !== this.props.options) {
            this.setState({ focusable: nextProps.options }, this.buildMenuOptions);
        }
        if (nextProps.search !== this.props.search && nextProps.search !== this.state.typedInput) {
            this.setState({ typedInput: nextProps.search || "" });
        }
    }

    componentDidMount() {
        this.buildMenuOptions();
    }

    componentDidUpdate() {
        this.scrollItemIntoView();
    }

    buildMenuOptions = (isOpening?: boolean /* Starter Custom Code */) => {
        if (!this.props.isLoading) {
            this.setState(prevState =>
                this.props.options.reduce<{ focusable: OptionObject[]; renderOptions: JSX.Element[] }>(
                    (menuState, option) => {
                        const { disabled, optionText } = option;
                        const optionValue =
                            option.optionValue !== undefined && option.optionValue !== null
                                ? option.optionValue
                                : optionText;

                        const searchString = option.searchString || optionText;
                        const resolvedMergeCss =
                            this.props.mergeCss ?? this.props?.theme?.dynamicDropdown?.defaultProps?.mergeCss;

                        if (
                            this.props.filterOption &&
                            !this.props.filterOption({ searchString, optionText }, prevState.typedInput)
                        ) {
                            return menuState;
                        }

                        const isFocused = !isOpening && prevState.focusedOption === option; // Starter Custom Code
                        const selected = prevState.selected === optionValue;
                        const newProps: OptionProps = {
                            key: optionValue,
                            uid: `${prevState.uid}-${optionText}`,
                            disabled,
                            focused: isFocused,
                            selected,
                            value: optionValue,
                            _sizeVariant: this.sizeVariant,
                            css: this.applyStyledProp(["cssOverrides", "option"], resolvedMergeCss),
                            onClick: disabled ? undefined : () => this.clickToSelect(optionValue, optionText),
                            // Starter Custom Code
                            role: "option",
                            id: optionValue,
                        };
                        if (isFocused) {
                            newProps.ref = this.focused;
                        }
                        if (selected) {
                            newProps["aria-selected"] = true;
                        }

                        menuState.focusable.push(option);
                        menuState.renderOptions.push(<Option {...newProps}>{option.rowChildren || optionText}</Option>);

                        return menuState;
                    },
                    { focusable: [], renderOptions: [] },
                ),
            );
        }
    };

    controlInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        !this.state.isOpen && this.openDropdown(undefined, true);
        this.props.onInputChange && this.props.onInputChange(event);
        this.setState({ typedInput: event.target.value }, this.buildMenuOptions);
    };

    clickToSelect = (value?: string, text?: string) => {
        this.setState({ selected: value, selectedText: text, typedInput: "" }, () => {
            this.closeDropdown(undefined, true);
            this.props.onSelectionChange && this.props.onSelectionChange(value);
            this.buildMenuOptions();
        });
    };

    closeDropdown = (event?: Event, callParentClose = false) => {
        callParentClose && this.popover.current && (this.popover.current! as any).closePopover();
        this.setState({ isOpen: false }, () => {
            this.props.onClose && this.props.onClose(event);
        });
    };

    handleKeyDown = (event: KeyboardEvent) => {
        if (!this.input.current?.contains(event.target as Node)) {
            this.closeDropdown(event);
            return;
        }
        event.stopPropagation();
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prior");
                break;
            case TAB_KEY:
                this.handleImplicitSelection("tab");
                break;
            case ENTER_KEY:
                this.handleImplicitSelection("enter");
                break;
            case ESC_KEY:
                // being handled in the popover;
                break;
            default:
                break;
        }
    };

    handleImplicitSelection = (eventType: "tab" | "enter") => {
        this.setState(
            ({ focusable, focusedOption, typedInput, selected, selectedText }) => {
                const newState = { selected, selectedText, typedInput: "" };

                let selectedOption: OptionObject | undefined;
                if (focusedOption && eventType === "enter") {
                    selectedOption = focusedOption;
                } else if (focusable.length === 1 && !focusable[0].disabled) {
                    selectedOption = focusable[0];
                } else if (focusable.length > 1 && typedInput !== "") {
                    selectedOption = focusable.find(option => option.optionText === typedInput && !option.disabled);
                }

                if (selectedOption) {
                    newState.selected = selectedOption.optionValue || selectedOption.optionText;
                    newState.selectedText = selectedOption.optionText || selectedOption.optionValue;
                }

                if (this.props.onSelectionChange && newState.selected !== selected) {
                    this.props.onSelectionChange(newState.selected);
                }

                return newState;
            },
            () => {
                this.closeDropdown(undefined, true);
                this.buildMenuOptions();
            },
        );
    };

    moveFocus = (direction: "prior" | "next") => {
        !this.state.isOpen && this.openDropdown(undefined, true);
        this.setState(({ focusedOption, focusable }) => {
            const focusableQueue = direction === "prior" ? focusable.reverse() : focusable;
            const focusedIndex = focusableQueue.indexOf(focusedOption as OptionObject);
            const newFocusedOption =
                focusableQueue.find((option, index) => index > focusedIndex && !option.disabled) || focusableQueue[0];
            return { focusedOption: newFocusedOption };
        }, this.buildMenuOptions);
    };

    openDropdown = (event?: Event, callParentOpen = false) => {
        callParentOpen && this.popover.current && (this.popover.current! as any).openPopover();
        this.setState(
            ({ focusable, selected }) => {
                const newState: Pick<DynamicDropdownState, "isOpen" | "focusedOption"> = { isOpen: true };
                selected &&
                    (newState.focusedOption = focusable.find(({ optionText, optionValue }) => {
                        return optionValue ? optionValue === selected : optionText === selected;
                    }));
                return newState;
            },
            () => {
                this.props.onOpen && this.props.onOpen(event);
                this.buildMenuOptions(true); // Starter Custom Code
                this.scrollListIntoView();
            },
        );
    };

    scrollItemIntoView = () => {
        // Scroll logic cribbed primarily from https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/utils.js#L190;
        const menuEl: HTMLElement | null = this.list.current;
        const itemEl: HTMLElement | null = this.focused.current;
        if (menuEl && itemEl) {
            const menuRect = menuEl.getBoundingClientRect();
            const focusedRect = itemEl.getBoundingClientRect();
            const overScroll = itemEl.offsetHeight / 3;

            if (focusedRect.bottom + overScroll > menuRect.bottom) {
                // going down
                scrollTo(
                    menuEl,
                    Math.min(
                        itemEl.offsetTop + itemEl.clientHeight - menuEl.offsetHeight + overScroll,
                        menuEl.scrollHeight,
                    ),
                );
            } else if (menuEl && focusedRect.top - overScroll < menuRect.top) {
                // going up
                scrollTo(menuEl, Math.max(itemEl.offsetTop - overScroll, 0));
            }
        }
    };

    scrollListIntoView = () => {
        /* Store Custom Code - Start */
        // We check "position: fixed" instead of "useOverlay" because some exceptions can still make it "absolute"
        const listElement = this.input.current?.querySelector(`[role="listbox"]`);

        if (listElement && getComputedStyle(listElement).position === "fixed") {
            return;
        }
        /* Store Custom Code - End */
        /* Starter Custom Code - Start */
        if (this.input.current) {
            const inputRect = this.input.current.getBoundingClientRect(); // Store Custom Code
            let scrollPosition: number | undefined;

            /* Store Custom Code - Start */
            const { scrollElement } = this.props;
            const elementRect = scrollElement?.getBoundingClientRect();
            const top = inputRect.top - (elementRect?.top || 0);
            const bottom = inputRect.bottom - (elementRect?.top || 0);
            const elementScrollTop = scrollElement?.scrollTop ?? 0;
            const innerHeight = scrollElement ? scrollElement.clientHeight : window.innerHeight;
            /* Store Custom Code - End */

            if (top < 0) {
                scrollPosition = top;
            } else {
                const windowHeight = innerHeight; // Store Custom Code
                const rowHeight = sizeVariantValues[this.sizeVariant].height;
                const heightOfRows =
                    (this.state.focusable.length ||
                        (!this.state.typedInput && this.props.hideNoOptionsIfEmptySearch ? 0 : 1) +
                            (this.props.moreOption ? 1 : 0)) * rowHeight;
                const listHeight = heightOfRows > 250 ? 250 : heightOfRows;
                const desiredBottom = bottom + listHeight + dynamicDropdownGap; // Store Custom Code

                if (desiredBottom - top > windowHeight) {
                    scrollPosition = top;
                } else if (desiredBottom > windowHeight) {
                    scrollPosition = desiredBottom - windowHeight;
                }
            }

            if (typeof scrollPosition !== "undefined") {
                /* Store Custom Code - Start */
                const scrollOffset = elementScrollTop ?? window.pageYOffset;
                const transitionLength = this.props.theme.transition.duration[defaultTransitionDuration] ?? 0;

                setTimeout(() => {
                    (scrollElement || window).scrollTo({
                        top: scrollPosition + scrollOffset,
                        behavior: "smooth",
                    });
                }, transitionLength + 60); // + to manage rendering delays
                /* Store Custom Code - End */
            }
        }
        /* Starter Custom Code - End */
    };

    render = () => {
        const {
            disable,
            disabled,
            error,
            filterOption,
            hint,
            isLoading,
            moreOption,
            onKeyPress,
            options,
            placeholder,
            selectOnly,
            sizeVariant,
            theme,
            hideNoOptionsIfEmptySearch,
            backgroundColor,
            showClear, // Starter Custom Code
            clearOnClick, // Starter Custom Code
            /* Store Custom Code - Start */
            useOverlay,
            /* Store Custom Code - End */
            ...otherProps
        } = this.props;
        const { isOpen, focusedOption, uid, typedInput, renderOptions, selected, selectedText } = this.state;

        const _cssOverrides = this.spreadProps("cssOverrides");

        // Because disabled html attribute doesn't accept undefined
        // eslint-disable-next-line no-unneeded-ternary
        const isDisabled = disable || disabled ? true : false;
        const listboxId = `${uid}-listbox`;
        const descriptionId = `${uid}-description`;
        const labelId = `${uid}-label`;
        const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
        const hasDescription = error || hint;
        const resolvedMergeCss = this.props.mergeCss ?? this.props?.theme?.dynamicDropdown?.defaultProps?.mergeCss;

        let renderList: JSX.Element[] = [
            <Option
                css={
                    this.applyStyledProp(["cssOverrides", "noOptions"], resolvedMergeCss) ||
                    this.applyStyledProp(["cssOverrides", "option"], resolvedMergeCss)
                }
                noHover
                _sizeVariant={this.sizeVariant}
                data-id="no-options"
                key="no-options"
                value="no-options"
            >
                {theme.translate("No Options")}
            </Option>,
        ];
        if (isLoading) {
            renderList = []; // Starter Custom Code - Moved Spinner into the field
        } else if (renderOptions && renderOptions.length > 0) {
            renderList = renderOptions;
        } else if (!typedInput && hideNoOptionsIfEmptySearch) {
            renderList = [];
        }

        /* Starter Custom Code - Start */
        const variantValues = sizeVariantValues[this.sizeVariant];
        const spinnerPosition = (variantValues.height - variantValues.icon) / 2;
        const spinnerProps = this.spreadProps("spinnerProps");
        spinnerProps.css = css`
            position: absolute;
            top: ${spinnerPosition}px;
            right: ${spinnerPosition}px;
            pointer-events: none;
            ${spinnerProps.css}
        `;

        const iconProps = this.spreadProps("iconProps");
        const hasIcon = Boolean(isLoading || iconProps?.src);

        const clearIconProps = this.spreadProps("clearIcon");
        clearIconProps.size = variantValues.icon;

        const clearClickableProps = this.spreadProps("clearClickable");
        const hasClear = showClear && clearOnClick;

        const iconClass = hasClear && hasIcon ? " hasTwoIcon" : hasClear || hasIcon ? " hasIcon" : "";
        const clearIconClass = hasIcon ? "icon-2 " : "";
        /* Starter Custom Code - End */

        const popoverTrigger = (
            <PopoverTriggerInput
                id={uid}
                type="text"
                role="combobox" // Starter Custom Code
                readOnly={selectOnly}
                aria-autocomplete="list"
                autoComplete="off" // Store Custom Code
                aria-activedescendant={focusedOption && focusedOption.optionValue} // Starter Custom Code
                aria-describedby={hasDescription ? descriptionId : undefined}
                aria-labelledby={labelId}
                onChange={this.controlInput}
                onKeyPress={onKeyPress}
                value={typedInput}
                placeholder={
                    selected
                        ? ""
                        : placeholder && DynamicDropdown.defaultPlaceholder === placeholder
                          ? theme.translate(placeholder)
                          : placeholder
                }
                disabled={isDisabled}
                data-test-selector={`${(otherProps as any)["data-test-selector"]}-input`}
                className={iconClass /* Starter Custom Code */}
                {...inputLabelObj}
                // Starter Custom Code
                aria-owns={listboxId}
                aria-haspopup={"listbox"}
                aria-expanded={isOpen}
            />
        );

        const triggerSiblings = (
            <>
                {typedInput ? null : !selected ? null : (
                    <SelectedText
                        className={`formField${iconClass}` /* Starter Custom Code */}
                        _sizeVariant={this.sizeVariant}
                        css={this.applyStyledProp(["cssOverrides", "selectedText"], resolvedMergeCss)}
                        data-test-selector={`${(otherProps as any)["data-test-selector"]}-selectedText`}
                    >
                        {selectedText || options?.find(o => o.optionValue === selected)?.optionText}
                    </SelectedText>
                )}
                {/* Starter Custom Code - Start */}
                {hasClear && (
                    <FormFieldClickable
                        {...clearClickableProps}
                        onClick={clearOnClick}
                        className={`${clearIconClass}${clearClickableProps.className}`}
                    >
                        <FormFieldIcon src="X" {...clearIconProps} />
                        <VisuallyHidden>{theme.translate("clear selection")}</VisuallyHidden>
                    </FormFieldClickable>
                )}
                <VisuallyHidden>
                    {isOpen ? theme.translate("hide options") : theme.translate("show options")}
                </VisuallyHidden>
                {isLoading ? (
                    <LoadingSpinner size={variantValues.icon} color="text.disabled" {...spinnerProps} />
                ) : (
                    <FormFieldIcon size={variantValues.icon} {...this.spreadProps("iconProps")} />
                )}
                {/* Starter Custom Code - End */}
            </>
        );

        /* Store Custom Code - Start */
        const positionFunction: PopoverProps["positionFunction"] = useOverlay
            ? ref => {
                  if (!ref.current) {
                      return { position: "absolute" };
                  }

                  const rect = ref.current.getBoundingClientRect();
                  const rowHeight = sizeVariantValues[this.sizeVariant].height;
                  const availableHeight = window.innerHeight - rect.bottom - 5; // 5 to not be entirely flush with the screen

                  if (availableHeight < rowHeight) {
                      return { position: "absolute" };
                  }

                  return {
                      left: `${rect.left}px`,
                      position: "fixed",
                      top: `${rect.bottom}px`,
                      zIndex: "999999",
                      width: `${rect.width}px`,
                      maxHeight: availableHeight < 250 ? `${availableHeight}px` : undefined,
                  };
              }
            : undefined;
        /* Store Custom Code - End */

        const comboBox = (
            <Popover
                ref={this.popover as any}
                controlsId={listboxId}
                onOpen={this.openDropdown}
                onClose={this.closeDropdown}
                handleKeyDown={this.handleKeyDown}
                popoverTrigger={popoverTrigger}
                triggerSiblings={triggerSiblings}
                transitionDuration={defaultTransitionDuration} // Store Custom Code
                xPosition="start"
                zIndexKey="dynamicDropdown"
                shadowDepth={1}
                insideRefs={[this.list, this.input]}
                wrapperProps={
                    {
                        as: "div",
                        /* Starter Removed - role */
                        /* Starter Removed - aria-owns */
                        /* Starter Removed - aria-haspopup */
                        /* Starter Removed - aria-expanded */
                        css: this.applyStyledProp(["cssOverrides", "dropdownWrapper"], resolvedMergeCss),
                        ref: this.input,
                        _width: "unset",
                        _height: "unset",
                    } as OverflowWrapperProps
                }
                contentBodyProps={
                    {
                        ref: this.list,
                        id: listboxId,
                        role: "listbox",
                        "aria-labelledby": labelId,
                        css: listCssBuilder(
                            this.applyStyledProp(["cssOverrides", "list"], resolvedMergeCss),
                            this.sizeVariant,
                        ) as any,
                        width: 400,
                    } as ContentBodyProps
                }
                data-test-selector={`${(otherProps as any)["data-test-selector"]}-listbox`}
                // Store Custom Code
                positionFunction={positionFunction}
            >
                {renderList}
                {!isLoading && moreOption ? (
                    <Option
                        key="more"
                        css={
                            this.applyStyledProp(["cssOverrides", "moreOption"], resolvedMergeCss) ||
                            this.applyStyledProp(["cssOverrides", "option"], resolvedMergeCss)
                        }
                        noHover
                        _sizeVariant={this.sizeVariant}
                        value="more options"
                    >
                        {moreOption}
                    </Option>
                ) : null}
            </Popover>
        );
        return (
            <FormField
                formInput={comboBox}
                inputId={uid}
                labelId={labelId}
                descriptionId={descriptionId}
                cssOverrides={_cssOverrides}
                sizeVariant={this.sizeVariant}
                error={error}
                backgroundColor={backgroundColor}
                {...otherProps}
            />
        );
    };
}

DynamicDropdown.displayName = "DynamicDropdown";
/** @component */
export default withDisabler(withTheme(DynamicDropdown));

export { Option };
